// qs-BrushTypePanel.vue
<template>
  <div class="brush-type-panel">
    <div 
      v-for="(brushType, index) in brushTypes" 
      :key="brushType.id"
      class="brush-type-item"
      :class="{ active: selectedIndex === index }"
      :tabindex="0"
      :aria-label="`Select ${brushType.name} brush`"
      role="button"
      @click="selectBrushType(index)"
      @keydown.enter="selectBrushType(index)"
      @keydown.space.prevent="selectBrushType(index)"
      @touchstart.passive="handleTouchStart(index)"
      @mouseleave="handleMouseLeave"
    >
      <div class="preview-area">
        <img
          :src="brushType.icon"
          :alt="`${brushType.name} brush icon`"
          class="brush-icon"
          :aria-label="`${brushType.name} brush icon`"
        >
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { getBrushIcon } from '@/assets/brush-icons'

type InternalBrushType = 'round' | 'square' | 'dashed' | 'pointed' | 'bow-tie' | 'thick-to-thin' | 'thin-to-thick' | 'spray'

interface BrushTypeItem {
  readonly id: string
  readonly type: InternalBrushType
  readonly name: string
  readonly lineCap: 'round' | 'butt' | 'square'
  readonly lineJoin: 'round' | 'miter' | 'bevel'
  readonly dashPattern?: readonly number[]
  readonly description: string
  readonly icon: string
}

interface Props {
  currentBrushType?: string
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  currentBrushType: 'round',
  disabled: false
})

const emit = defineEmits<{
  'brush-type-changed': [brushType: string, index: number]
  'brush-type-selected': [brushType: BrushTypeItem]
}>()

const selectedIndex = ref(0)
const isInitialized = ref(false)

const brushTypes = ref<BrushTypeItem[]>([
  {
    id: 'round',
    type: 'round',
    name: 'Round',
    lineCap: 'round',
    lineJoin: 'round',
    description: 'Smooth round brush with soft edges',
    icon: getBrushIcon('round')
  },
  {
    id: 'square',
    type: 'square',
    name: 'Square',
    lineCap: 'square',
    lineJoin: 'miter',
    description: 'Sharp square brush with hard edges',
    icon: getBrushIcon('square')
  },
  {
    id: 'dashed',
    type: 'dashed',
    name: 'Dashed',
    lineCap: 'round',
    lineJoin: 'round',
    description: 'Dashed line brush with gaps',
    icon: getBrushIcon('dashed')
  },
  {
    id: 'pointed',
    type: 'pointed',
    name: 'Pointed',
    lineCap: 'butt',
    lineJoin: 'miter',
    description: 'Sharp pointed brush with clean ends',
    icon: getBrushIcon('pointed')
  },
  {
    id: 'bow-tie',
    type: 'bow-tie',
    name: 'Bow-Tie',
    lineCap: 'round',
    lineJoin: 'round',
    description: 'Thick at ends, thin in middle',
    icon: getBrushIcon('bow-tie')
  },
  {
    id: 'thick-to-thin',
    type: 'thick-to-thin',
    name: 'Thick→Thin',
    lineCap: 'round',
    lineJoin: 'round',
    description: 'Starts thick and tapers to thin',
    icon: getBrushIcon('thick-to-thin')
  },
  {
    id: 'thin-to-thick',
    type: 'thin-to-thick',
    name: 'Thin→Thick',
    lineCap: 'round',
    lineJoin: 'round',
    description: 'Starts thin and tapers to thick',
    icon: getBrushIcon('thin-to-thick')
  },
  {
    id: 'spray',
    type: 'spray',
    name: 'Spray',
    lineCap: 'round',
    lineJoin: 'round',
    description: 'Random spray dots around the line',
    icon: getBrushIcon('spray')
  }
])

const isValidIndex = (index: number): boolean => {
  return index >= 0 && index < brushTypes.value.length
}

const initializeBrushType = (): void => {
  if (!props.currentBrushType) return
  
  const index = brushTypes.value.findIndex(bt => bt.type === props.currentBrushType)
  if (index !== -1) {
    selectedIndex.value = index
  } else {
    console.warn('BrushTypePanel: Unknown brush type from props', props.currentBrushType)
  }
}

const selectBrushType = (index: number): void => {
  if (props.disabled || !isValidIndex(index)) {
    console.warn('BrushTypePanel: Invalid brush type selection', { index, disabled: props.disabled })
    return
  }

  selectedIndex.value = index
  const brushType = brushTypes.value[index]
  
  if (brushType) {
    emit('brush-type-changed', brushType.type, index)
    emit('brush-type-selected', brushType)
  }
}

const handleTouchStart = (index: number): void => {
  if (props.disabled) return
  selectBrushType(index)
}

const handleMouseLeave = (event: MouseEvent): void => {
  const target = event.target as HTMLElement
  if (target?.classList.contains('brush-type-item')) {
    target.blur()
  }
}

watch(() => props.currentBrushType, (newType) => {
  if (newType && isInitialized.value) {
    initializeBrushType()
  }
}, { immediate: false })

onMounted(() => {
  try {
    initializeBrushType()
    isInitialized.value = true

    if (isValidIndex(selectedIndex.value)) {
      const brushType = brushTypes.value[selectedIndex.value]
      if (brushType) {
        emit('brush-type-changed', brushType.type, selectedIndex.value)
        emit('brush-type-selected', brushType)
      }
    }
  } catch (error) {
    console.error('BrushTypePanel: Error during initialization', error)
  }
})
</script>

<style scoped>
.brush-type-panel {
  display: flex;
  flex-direction: column;
  width: auto;
  min-width: fit-content;
  background: transparent;
  gap: 0;
  position: relative;
  user-select: none;
}

.brush-type-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: theme('spacing.11');
  height: theme('spacing.11');
  background: transparent;
  cursor: pointer;
  padding: 0;
  position: relative;
  transition: background-color 0.15s ease;
  outline: none;
  min-height: theme('spacing.11');
}

.brush-type-item:hover:not(:disabled):not(:focus) {
  background: theme('colors.amber.50');
}

.brush-type-item:focus:not(:disabled) {
  background: theme('colors.gray.300');
  outline: none;
}

.brush-type-item:focus-visible {
  outline: none;
}

.brush-type-item.active {
  background: #fcfcfc;
}

.brush-type-item:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.preview-area {
  width: theme('spacing.8');
  height: theme('spacing.8');
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.brush-icon {
  width: 20px !important;
  height: 20px !important;
  color: theme('colors.gray.800');
  transition: color 0.15s ease;
}

.brush-type-item.active .brush-icon {
  color: theme('colors.gray.800');
}

.brush-type-item:hover:not(:disabled) .brush-icon {
  color: theme('colors.gray.800');
}

@media (hover: none) and (pointer: coarse) {
  .brush-type-item {
    min-height: theme('spacing.11');
    padding: theme('spacing.1.5');
  }
  
  .brush-type-item:active {
    background: theme('colors.gray.300');
    transform: scale(0.98);
  }
}

@media (prefers-contrast: high) {
  .brush-type-item.active {
    background: theme('colors.black');
    color: theme('colors.creme.light');
  }
}

@media (prefers-reduced-motion: reduce) {
  .brush-type-item {
    transition: none;
  }
}
</style>
